// apps/client/src/components/command-palette/CommandPalette.tsx
// Spotlight-style command palette for searching pages and entities

import React, { useState, useEffect, useMemo } from 'react';
import { Command } from 'cmdk';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Loader2,
  Building2,
  UserCircle,
  Banknote,
  ArrowRight,
} from 'lucide-react';

import { useCommandPalette } from './CommandPaletteProvider';
import { useEntitySearch } from './useEntitySearch';
import {
  SEARCHABLE_PAGES,
  SECTION_CONFIG,
  getVisiblePages,
  searchPages,
  type SearchablePage,
  type UserRole,
  type SubscriptionTier,
} from './pageIndex';
import { useAuth } from '../../hooks/useAuth';
import { adminPathDenied } from '../../config/adminAccess';

// Format cents as currency
function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

interface CommandPaletteProps {
  subscriptionTier?: SubscriptionTier;
  /** `companyData.featureFlags`, so the palette drops tenant-suppressed reports. */
  featureFlags?: Record<string, unknown> | null;
}

export function CommandPalette({ subscriptionTier = 'plus', featureFlags }: CommandPaletteProps) {
  const { isOpen, close } = useCommandPalette();
  const navigate = useNavigate();
  const { claims, companyId } = useAuth();
  const [query, setQuery] = useState('');

  // Get user role from claims
  const userRole = (claims?.role as UserRole) || undefined;
  // Marketing is denied broker / office / loan / customer (entity) search, so
  // the palette's entity lookup is disabled for them (marketing RBAC §6
  // entity-search lockdown). They keep page search over allow-listed pages.
  const isMarketing = userRole === 'marketing';

  // Filter pages based on user permissions
  const visiblePages = useMemo(
    () =>
      getVisiblePages(userRole, subscriptionTier, featureFlags).filter(
        (page) => !adminPathDenied(claims, page.path),
      ),
    [userRole, subscriptionTier, featureFlags, claims]
  );

  // Search pages locally (instant)
  const filteredPages = useMemo(
    () => searchPages(visiblePages, query),
    [visiblePages, query]
  );

  // Search entities via API (debounced). Disabled for marketing, which has no
  // access to broker / office / loan / customer records.
  const entityResults = useEntitySearch({
    query,
    companyId,
    enabled: isOpen && query.length >= 2 && !isMarketing,
  });

  // Group pages by section
  const groupedPages = useMemo(() => {
    const groups: Record<string, SearchablePage[]> = {};
    for (const page of filteredPages) {
      if (!groups[page.section]) {
        groups[page.section] = [];
      }
      groups[page.section].push(page);
    }
    return groups;
  }, [filteredPages]);

  // Reset query when closed
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
    }
  }, [isOpen]);

  // Handle navigation
  const handleSelect = (path: string) => {
    close();
    navigate(path);
  };

  // Handle dynamic office selection
  const handleOfficeSelect = (officeId: string) => {
    close();
    navigate(`/offices/${officeId}`);
  };

  // Handle broker selection
  const handleBrokerSelect = (brokerId: string) => {
    close();
    navigate(`/team?broker=${brokerId}`);
  };

  // Handle loan selection
  const handleLoanSelect = (loanId: string) => {
    close();
    navigate(`/reports/loans?search=${encodeURIComponent(loanId)}`);
  };

  if (!isOpen) return null;

  const hasEntityResults =
    entityResults.brokers.length > 0 ||
    entityResults.offices.length > 0 ||
    entityResults.loans.length > 0;

  const hasPageResults = filteredPages.length > 0;
  const hasResults = hasPageResults || hasEntityResults;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
        onClick={close}
      />

      {/* Dialog */}
      <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]">
        <Command
          className="w-full max-w-[640px] bg-gray-900 border border-gray-700 rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200"
          shouldFilter={false}
          loop
        >
          {/* Search Input */}
          <div className="flex items-center border-b border-gray-700 px-4">
            <Search className="w-5 h-5 text-gray-500 flex-shrink-0" />
            <Command.Input
              value={query}
              onValueChange={setQuery}
              placeholder="Search pages, brokers, offices, loans..."
              className="flex-1 h-14 bg-transparent text-white placeholder-gray-500 border-none outline-none text-base px-3"
              autoFocus
            />
            <div className="flex items-center gap-2 flex-shrink-0">
              {entityResults.isLoading && (
                <Loader2 className="w-4 h-4 text-gray-500 animate-spin" />
              )}
              <kbd className="text-xs text-gray-500 bg-gray-800 px-2 py-1 rounded">
                ESC
              </kbd>
            </div>
          </div>

          {/* Results */}
          <Command.List className="max-h-[400px] overflow-y-auto p-2">
            {/* Empty state */}
            {query.length >= 2 && !hasResults && !entityResults.isLoading && (
              <Command.Empty className="py-8 text-center text-gray-500">
                No results found for "{query}"
              </Command.Empty>
            )}

            {/* Initial state - show popular pages */}
            {query.length < 2 && (
              <Command.Group
                heading={
                  <span className="text-xs text-gray-500 font-medium px-2">
                    Quick Navigation
                  </span>
                }
              >
                {visiblePages.slice(0, 6).map((page) => (
                  <PageItem
                    key={page.id}
                    page={page}
                    onSelect={() => handleSelect(page.path)}
                  />
                ))}
              </Command.Group>
            )}

            {/* Page Results */}
            {query.length >= 2 &&
              Object.entries(SECTION_CONFIG)
                .sort(([, a], [, b]) => a.order - b.order)
                .map(([section, config]) => {
                  const pages = groupedPages[section];
                  if (!pages || pages.length === 0) return null;

                  return (
                    <Command.Group
                      key={section}
                      heading={
                        <span className="text-xs text-gray-500 font-medium px-2">
                          {config.name}
                        </span>
                      }
                    >
                      {pages.map((page) => (
                        <PageItem
                          key={page.id}
                          page={page}
                          onSelect={() => {
                            if (page.isDynamic) {
                              // For dynamic routes, just navigate to the base path
                              handleSelect(page.path.split('/:')[0]);
                            } else {
                              handleSelect(page.path);
                            }
                          }}
                        />
                      ))}
                    </Command.Group>
                  );
                })}

            {/* Broker Results */}
            {entityResults.brokers.length > 0 && (
              <Command.Group
                heading={
                  <span className="text-xs text-gray-500 font-medium px-2">
                    Brokers
                  </span>
                }
              >
                {entityResults.brokers.map((broker) => (
                  <Command.Item
                    key={broker.id}
                    value={`broker-${broker.id}`}
                    onSelect={() => handleBrokerSelect(broker.id)}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer text-gray-300 hover:bg-gray-800 hover:text-white data-[selected=true]:bg-blue-600/20 data-[selected=true]:text-white outline-none"
                  >
                    <UserCircle className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="truncate">{broker.name}</div>
                      <div className="text-xs text-gray-500 truncate">
                        {broker.officeName}
                      </div>
                    </div>
                    <span
                      className={`text-xs px-1.5 py-0.5 rounded ${
                        broker.status === 'active'
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-gray-500/20 text-gray-400'
                      }`}
                    >
                      {broker.status}
                    </span>
                    <ArrowRight className="w-3 h-3 text-gray-600" />
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {/* Office Results */}
            {entityResults.offices.length > 0 && (
              <Command.Group
                heading={
                  <span className="text-xs text-gray-500 font-medium px-2">
                    Offices
                  </span>
                }
              >
                {entityResults.offices.map((office) => (
                  <Command.Item
                    key={office.id}
                    value={`office-${office.id}`}
                    onSelect={() => handleOfficeSelect(office.id)}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer text-gray-300 hover:bg-gray-800 hover:text-white data-[selected=true]:bg-blue-600/20 data-[selected=true]:text-white outline-none"
                  >
                    <Building2 className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="truncate">{office.name}</div>
                      <div className="text-xs text-gray-500">
                        {office.brokerCount} broker
                        {office.brokerCount !== 1 ? 's' : ''}
                      </div>
                    </div>
                    <ArrowRight className="w-3 h-3 text-gray-600" />
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {/* Loan Results */}
            {entityResults.loans.length > 0 && (
              <Command.Group
                heading={
                  <span className="text-xs text-gray-500 font-medium px-2">
                    Loans
                  </span>
                }
              >
                {entityResults.loans.map((loan) => (
                  <Command.Item
                    key={loan.loanId}
                    value={`loan-${loan.loanId}`}
                    onSelect={() => handleLoanSelect(loan.loanId)}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer text-gray-300 hover:bg-gray-800 hover:text-white data-[selected=true]:bg-blue-600/20 data-[selected=true]:text-white outline-none"
                  >
                    <Banknote className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="truncate font-mono text-sm">
                        {loan.loanId}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {loan.borrowerName} · {loan.lender}
                      </div>
                    </div>
                    <span className="text-xs text-gray-400">
                      {formatCurrency(loan.loanAmountCents)}
                    </span>
                    <ArrowRight className="w-3 h-3 text-gray-600" />
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {/* Keyboard hints */}
            <div className="flex items-center justify-center gap-4 py-3 text-xs text-gray-600 border-t border-gray-800 mt-2">
              <span className="flex items-center gap-1">
                <kbd className="bg-gray-800 px-1.5 py-0.5 rounded">↑↓</kbd>
                navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="bg-gray-800 px-1.5 py-0.5 rounded">↵</kbd>
                select
              </span>
              <span className="flex items-center gap-1">
                <kbd className="bg-gray-800 px-1.5 py-0.5 rounded">esc</kbd>
                close
              </span>
            </div>
          </Command.List>
        </Command>
      </div>
    </>
  );
}

// Page item component
interface PageItemProps {
  page: SearchablePage;
  onSelect: () => void;
}

function PageItem({ page, onSelect }: PageItemProps) {
  const Icon = page.icon;

  return (
    <Command.Item
      value={page.id}
      onSelect={onSelect}
      className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer text-gray-300 hover:bg-gray-800 hover:text-white data-[selected=true]:bg-blue-600/20 data-[selected=true]:text-white outline-none"
    >
      <Icon className="w-4 h-4 text-gray-500 flex-shrink-0" />
      <span className="flex-1 truncate">{page.name}</span>
      {page.requiredTier && page.requiredTier !== 'plus' && (
        <span
          className={`text-[0.625rem] px-1.5 py-0.5 rounded font-medium ${
            page.requiredTier === 'pro'
              ? 'bg-purple-500/20 text-purple-400'
              : 'bg-amber-500/20 text-amber-400'
          }`}
        >
          {page.requiredTier.toUpperCase()}
        </span>
      )}
      <ArrowRight className="w-3 h-3 text-gray-600" />
    </Command.Item>
  );
}
